import { type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";

interface ProtectedRouteProps {
  children: ReactNode;
  requireEmailVerified?: boolean;
}

export function ProtectedRoute({ children, requireEmailVerified = false }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: "48px",
              height: "48px",
              border: "4px solid #f3f3f3",
              borderTop: "4px solid #3498db",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto",
            }}
          />
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
          <p style={{ marginTop: "16px", color: "#666" }}>Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to /login if not authenticated, preserving the attempted URL
  // Use a query parameter so the return URL survives page refreshes
  if (!user) {
    const returnUrl = location.pathname + location.search + location.hash;
    const loginTarget =
      returnUrl && returnUrl !== "/" && returnUrl !== "/dashboard"
        ? `/login?returnUrl=${encodeURIComponent(returnUrl)}`
        : "/login";
    return <Navigate to={loginTarget} state={{ from: location }} replace />;
  }

  // Redirect to /verify-email if email not verified and required
  if (requireEmailVerified && !user.emailVerified) {
    const returnUrl = location.pathname + location.search + location.hash;
    const verifyTarget =
      returnUrl && returnUrl !== "/" && returnUrl !== "/dashboard"
        ? `/verify-email?returnUrl=${encodeURIComponent(returnUrl)}`
        : "/verify-email";
    return <Navigate to={verifyTarget} state={{ from: location }} replace />;
  }

  // Render children if authenticated (and verified if required)
  return <>{children}</>;
}
